import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { makeEmployeeLoaderWithDetails } from 'helpers/debounce';
import {
  CustomOption,
  CustomMultiValue,
  CustomPlaceholder,
} from './EmployeeSelectField';

/**
 * EmployeeRecipientSelect — To / CC picker for the compose dialogs whose backend
 * resolves recipients from EMPLOYEE CODES (`Organization::Employee.where(name:)`),
 * not from addresses. It therefore stores and emits a `string[]` of codes.
 *
 * For the release-forecast endpoints, which take plain addresses, use
 * `components/common/select/RecipientSelect` instead — that one emits emails.
 *
 * Each dropdown row shows avatar + code + full name + email, and each selected
 * chip an avatar + code, via the shared renderers in EmployeeSelectField.
 */

// The dropdown is portalled to <body> instead of rendering inline: inside a
// compose card it gets clipped by the card's `overflow-hidden` and paints under
// the Subject row and the TinyMCE editor. Modals sit at z-index 100000 and
// TinyMCE's floating UI at 100001 (see src/tailwind.css), so the menu clears both.
const MENU_Z_INDEX = 100002;

const selectStyles = (hasError) => ({
  // Borderless control — this select sits inside the compose card's own bordered
  // To/CC rows, so it shouldn't draw a second border. An error still needs to be
  // visible, hence the ring when `hasError`.
  control: (base) => ({
    ...base,
    border: 'none',
    boxShadow: hasError ? '0 0 0 2px rgba(239,68,68,0.35)' : 'none',
    minHeight: 38,
  }),
  menuPortal: (base) => ({ ...base, zIndex: MENU_Z_INDEX }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  }),
  menuList: (base) => ({ ...base, padding: 4, maxHeight: 280 }),
  // CustomOption paints its own padding/background, so strip react-select's.
  option: (base) => ({
    ...base,
    padding: 0,
    backgroundColor: 'transparent',
    '&:active': { backgroundColor: 'transparent' },
  }),
});

const EmployeeRecipientSelect = ({
  value,
  onChange,
  placeholder,
  employeeType,
  hasError,
  isDisabled,
}) => {
  // One debounced loader per instance, so two selects on the same dialog don't
  // share a debounce timer and cancel each other's search.
  const loadOptions = useRef(makeEmployeeLoaderWithDetails(employeeType)).current;

  // `value` is only a list of codes, so remember the full option behind each code
  // as it is picked — otherwise a chip loses its avatar and name on the next render.
  const cache = useRef({});

  const selected = useMemo(
    () => (Array.isArray(value) ? value : []).map(
      (code) => cache.current[code] || { value: code, label: code }
    ),
    [value]
  );

  const handleChange = (options) => {
    const list = options || [];
    list.forEach((option) => { if (option?.value) cache.current[option.value] = option; });
    onChange(list.map((option) => option.value));
  };

  // menuPlacement auto: a To/CC row near the bottom of a drawer or modal
  // would otherwise open its menu under the footer.
  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      isMulti
      closeMenuOnSelect={false}
      isDisabled={isDisabled}
      loadOptions={loadOptions}
      value={selected}
      onChange={handleChange}
      placeholder={placeholder}
      classNamePrefix="react-select"
      styles={selectStyles(hasError)}
      components={{
        Option: CustomOption,
        MultiValue: CustomMultiValue,
        Placeholder: CustomPlaceholder,
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      menuPlacement="auto"
      maxMenuHeight={260}
    />
  );
};

EmployeeRecipientSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  employeeType: PropTypes.string,
  hasError: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

EmployeeRecipientSelect.defaultProps = {
  value: [],
  placeholder: 'Select recipients…',
  employeeType: 'all',
  hasError: false,
  isDisabled: false,
};

export default EmployeeRecipientSelect;
